import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Divider,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  Container
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Psychology as PsychologyIcon,
  TextFields as TextFieldsIcon,
  Undo as UndoIcon,
  InfoOutlined as InfoIcon,
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { AuthContext } from '../App';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';

// Settings categories with their configurations
const settingsConfig = {
  ai_analysis: {
    title: 'AI Analysis',
    icon: <PsychologyIcon />,
    color: '#4fc3f7',
    description: 'Configure AI-powered reading analysis',
    settings: [
      {
        key: 'gpt_analysis_instruction',
        label: 'GPT Analysis Instruction Prompt',
        description: 'Customize the instructions given to GPT for analyzing student reading performance',
        type: 'textarea',
        rows: 20,
        required: true
      }
    ]
  },
  general: {
    title: 'General',
    icon: <SettingsIcon />,
    color: '#81c784',
    description: 'Basic application settings',
    settings: [
      {
        key: 'app_name',
        label: 'Application Name',
        description: 'The name displayed in the application header',
        type: 'text',
        required: true
      }
    ]
  },
  grading: {
    title: 'Grading',
    icon: <TextFieldsIcon />,
    color: '#ffb74d',
    description: 'Grading method preferences',
    settings: [
      {
        key: 'default_grading_method',
        label: 'Default Grading Method',
        description: 'Default method for grading new tests',
        type: 'select',
        options: [
          { value: 'whisper', label: 'Whisper AI' },
          { value: 'speechace', label: 'SpeechAce' }
        ],
        required: true
      }
    ]
  }
};

export default function Settings() {
  const { teacher } = useContext(AuthContext);
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeCategory, setActiveCategory] = useState('ai_analysis');
  const [unsavedChanges, setUnsavedChanges] = useState({});
  const [activePromptVersion, setActivePromptVersion] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchActivePromptVersion();
  }, []);

  const fetchActivePromptVersion = async () => {
    try {
      const response = await api.get('/api/prompt-versions/active');
      setActivePromptVersion(response.data);
    } catch (error) {
      console.log('No active prompt version found or error:', error);
      setActivePromptVersion(null);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/settings');
      setSettings(response.data);
      setUnsavedChanges({});
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load settings',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setUnsavedChanges(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getCurrentValue = (key) => {
    return unsavedChanges.hasOwnProperty(key) 
      ? unsavedChanges[key] 
      : settings[key]?.value || '';
  };

  const saveSetting = async (key) => {
    if (!unsavedChanges.hasOwnProperty(key)) return;

    try {
      setSaving(true);
      const value = unsavedChanges[key];
      
      await api.put(`/api/settings/${key}`, {
        value: value,
        description: getSettingConfig(key)?.description,
        category: getCategoryByKey(key)
      });

      // Update local settings
      setSettings(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          value: value,
          updatedBy: teacher,
          updatedAt: new Date().toISOString()
        }
      }));

      // Remove from unsaved changes
      setUnsavedChanges(prev => {
        const newChanges = { ...prev };
        delete newChanges[key];
        return newChanges;
      });

      setSnackbar({
        open: true,
        message: 'Setting saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving setting:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save setting',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const resetSetting = (key) => {
    setUnsavedChanges(prev => {
      const newChanges = { ...prev };
      delete newChanges[key];
      return newChanges;
    });
  };

  const getSettingConfig = (key) => {
    for (const category of Object.values(settingsConfig)) {
      const setting = category.settings.find(s => s.key === key);
      if (setting) return setting;
    }
    return null;
  };

  const getCategoryByKey = (key) => {
    for (const [categoryKey, category] of Object.entries(settingsConfig)) {
      if (category.settings.some(s => s.key === key)) {
        return categoryKey;
      }
    }
    return 'general';
  };

  const hasUnsavedChanges = (key) => {
    return unsavedChanges.hasOwnProperty(key);
  };

  const renderSettingField = (settingConfig) => {
    const { key, label, type, rows, options, required } = settingConfig;
    const value = getCurrentValue(key);
    const hasChanges = hasUnsavedChanges(key);

    // Special handling for GPT analysis instruction - link to version management
    if (key === 'gpt_analysis_instruction') {
      return (
        <Card variant="outlined" sx={{ p: 3, bgcolor: 'background.default' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Prompt Version Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                GPT analysis prompts are managed through a comprehensive version control system.
              </Typography>
            </Box>
            <Chip
              label={activePromptVersion ? `Active: v${activePromptVersion.version}` : 'No Active Version'}
              color="success"
              sx={{ fontWeight: 600 }}
            />
          </Box>

          {activePromptVersion && (
            <>
              <Divider sx={{ my: 2 }} />

              {/* Version Info */}
              <Stack spacing={1} sx={{ mb: 2 }}>
                {activePromptVersion.label && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Label:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {activePromptVersion.label}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Created:</Typography>
                  <Typography variant="body2">
                    {new Date(activePromptVersion.createdAt).toLocaleString()}
                    {activePromptVersion.createdBy && ` by ${activePromptVersion.createdBy.name}`}
                  </Typography>
                </Box>
                {activePromptVersion.activatedAt && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Activated:</Typography>
                    <Typography variant="body2">
                      {new Date(activePromptVersion.activatedAt).toLocaleString()}
                      {activePromptVersion.activatedBy && ` by ${activePromptVersion.activatedBy.name}`}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Character count:</Typography>
                  <Typography variant="body2">{activePromptVersion.content.length} characters</Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Full Prompt Content */}
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Current Prompt Content:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 400, overflow: 'auto', bgcolor: '#f5f5f5' }}>
                <Typography component="pre" sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  margin: 0,
                  lineHeight: 1.5
                }}>
                  {activePromptVersion.content}
                </Typography>
              </Paper>
            </>
          )}

          <Button
            variant="contained"
            startIcon={<HistoryIcon />}
            onClick={() => navigate('/prompt-versions')}
            sx={{ fontWeight: 600 }}
            fullWidth
          >
            Manage Prompt Versions
          </Button>
        </Card>
      );
    }

    if (type === 'textarea') {
      return (
        <TextField
          fullWidth
          multiline
          rows={rows || 4}
          label={label}
          value={value}
          onChange={(e) => handleSettingChange(key, e.target.value)}
          required={required}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '13px',
              lineHeight: 1.4,
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
            }
          }}
        />
      );
    }

    if (type === 'select') {
      return (
        <TextField
          fullWidth
          select
          label={label}
          value={value}
          onChange={(e) => handleSettingChange(key, e.target.value)}
          required={required}
          variant="outlined"
          SelectProps={{
            native: true
          }}
        >
          <option value="">Select...</option>
          {options?.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </TextField>
      );
    }

    return (
      <TextField
        fullWidth
        label={label}
        value={value}
        onChange={(e) => handleSettingChange(key, e.target.value)}
        required={required}
        variant="outlined"
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  const activeConfig = settingsConfig[activeCategory];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #e0f7fa 0%, #fffde7 100%)',
      fontFamily: 'Nunito, sans-serif'
    }}>
      {/* Header */}
      <Box sx={{ 
        background: '#fff', 
        borderBottom: '1px solid #e0e0e0',
        px: 4,
        py: 3
      }}>
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton 
                onClick={() => navigate('/dashboard')}
                sx={{ 
                  backgroundColor: '#f5f5f5', 
                  '&:hover': { backgroundColor: '#e0e0e0' } 
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Avatar sx={{ bgcolor: '#4fc3f7', width: 48, height: 48 }}>
                <SettingsIcon sx={{ fontSize: 24 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight={700} color="#333">
                  System Settings
                </Typography>
                <Typography color="text.secondary" fontSize={16}>
                  Customize your reading coach application
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchSettings}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Refresh
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" gap={4}>
          {/* Sidebar Navigation */}
          <Paper sx={{ 
            width: 280, 
            height: 'fit-content',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <Box sx={{ p: 3, background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="h6" fontWeight={700} color="#333">
                Categories
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {Object.entries(settingsConfig).map(([categoryKey, category]) => (
                <ListItem key={categoryKey} disablePadding>
                  <ListItemButton
                    selected={activeCategory === categoryKey}
                    onClick={() => setActiveCategory(categoryKey)}
                    sx={{
                      py: 2,
                      px: 3,
                      '&.Mui-selected': {
                        backgroundColor: `${category.color}15`,
                        borderRight: `3px solid ${category.color}`,
                        '& .MuiListItemIcon-root': {
                          color: category.color
                        },
                        '& .MuiListItemText-primary': {
                          color: category.color,
                          fontWeight: 700
                        }
                      },
                      '&:hover': {
                        backgroundColor: `${category.color}08`
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {category.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={category.title}
                      secondary={category.description}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: '15px'
                      }}
                      secondaryTypographyProps={{
                        fontSize: '12px'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Content Area */}
          <Box flex={1}>
            <Paper sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}>
              {/* Content Header */}
              <Box sx={{ 
                p: 4, 
                background: `linear-gradient(135deg, ${activeConfig.color}15 0%, ${activeConfig.color}05 100%)`,
                borderBottom: '1px solid #e0e0e0'
              }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar sx={{ bgcolor: activeConfig.color, width: 56, height: 56 }}>
                    {activeConfig.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="#333">
                      {activeConfig.title} Settings
                    </Typography>
                    <Typography color="text.secondary" fontSize={16}>
                      {activeConfig.description}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Settings Content */}
              <Box sx={{ p: 4 }}>
                {activeConfig.settings.map((settingConfig, index) => {
                  const { key, description } = settingConfig;
                  const hasChanges = hasUnsavedChanges(key);
                  const settingData = settings[key];
                  
                  return (
                    <Card 
                      key={key}
                      sx={{ 
                        mb: index < activeConfig.settings.length - 1 ? 4 : 0,
                        borderRadius: 2,
                        border: hasChanges ? `2px solid ${activeConfig.color}` : '1px solid #e0e0e0',
                        boxShadow: hasChanges ? `0 4px 12px ${activeConfig.color}20` : '0 2px 8px rgba(0,0,0,0.04)'
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" gap={2} mb={1}>
                              <Typography variant="h6" fontWeight={600}>
                                {settingConfig.label}
                              </Typography>
                              {hasChanges && (
                                <Chip 
                                  label="Unsaved Changes" 
                                  size="small" 
                                  color="warning"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {description}
                            </Typography>
                          </Box>
                          
                          <Tooltip title="Setting Information">
                            <IconButton size="small">
                              <InfoIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        {renderSettingField(settingConfig)}

                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
                          <Box>
                            {settingData?.updatedBy && (
                              <Typography variant="caption" color="text.secondary">
                                Last updated by <strong>{settingData.updatedBy.name}</strong> on{' '}
                                {new Date(settingData.updatedAt).toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                          
                          <Stack direction="row" spacing={2}>
                            {hasChanges && (
                              <Button
                                variant="outlined"
                                startIcon={<UndoIcon />}
                                onClick={() => resetSetting(key)}
                                sx={{ borderRadius: 2, fontWeight: 600 }}
                              >
                                Reset
                              </Button>
                            )}
                            <Button
                              variant="contained"
                              startIcon={<SaveIcon />}
                              onClick={() => saveSetting(key)}
                              disabled={!hasChanges || saving}
                              sx={{
                                borderRadius: 2,
                                fontWeight: 600,
                                minWidth: 100,
                                background: hasChanges 
                                  ? `linear-gradient(90deg, ${activeConfig.color} 0%, ${activeConfig.color}dd 100%)`
                                  : undefined
                              }}
                            >
                              {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                            </Button>
                          </Stack>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 